import React from 'react';
import { useContactCategoryEntryScreen } from './hooks/useContactCategoryEntryScreen';

export default function ContactCategoryEntryScreen() {
  const { state, actions } = useContactCategoryEntryScreen();

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Masters</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span>Content & Communication</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <button
              type="button"
              onClick={actions.handleCancel}
              className="text-on-surface-variant hover:text-primary transition cursor-pointer"
            >
              Contact Category Master
            </button>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">
              {state.isEditMode ? `Edit Category #${state.id}` : 'New Category'}
            </span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Contact Category' : 'Create Contact Category'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Define stakeholder classifications for CRM contacts, cloud telephony lookups, and account directories.
          </p>
        </div>

        {/* Action Buttons Top */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={actions.handleCancel}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center rounded-xl border border-outline-variant/40 bg-surface px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50"
          >
            {state.isSaving && (
              <span className="material-symbols-outlined animate-spin text-sm">
                progress_activity
              </span>
            )}
            <span>{state.isEditMode ? 'Update Category' : 'Save Category'}</span>
          </button>
        </div>
      </section>

      {/* Main Grid Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Form Card */}
        <div className="lg:col-span-2 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-xs flex flex-col gap-6">
          <h2 className="text-sm font-bold text-on-surface flex items-center gap-2 border-b border-outline-variant/15 pb-3">
            <span className="material-symbols-outlined text-primary text-lg">badge</span>
            <span>Category Definition</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Category Name */}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-on-surface">
                Category Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={state.form.name || ''}
                onChange={(e) => actions.patchForm('name', e.target.value)}
                placeholder="e.g. Client, Vendor, Employee, Channel Partner"
                maxLength={100}
                required
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
              />
              <span className="text-[10px] text-on-surface-variant font-medium">
                Unique name identifying the contact or stakeholder group.
              </span>
            </div>

            {/* Operational Status */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-on-surface">
                Operational Status
              </label>
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-surface-container-high w-fit shadow-xs">
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('active')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer select-none ${
                    state.operationalStatus === 'active'
                      ? 'bg-primary text-on-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('inactive')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer select-none ${
                    state.operationalStatus === 'inactive'
                      ? 'bg-primary text-on-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Inactive
                </button>
              </div>
              <span className="text-[10px] text-on-surface-variant font-medium">
                Archived categories are excluded from new contact assignments.
              </span>
            </div>

            {/* Description */}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-on-surface">
                Description / Scope
              </label>
              <textarea
                rows={3}
                value={state.form.description || ''}
                onChange={(e) => actions.patchForm('description', e.target.value)}
                placeholder="Optional notes regarding the scope, usage, or escalation rules for this contact category..."
                maxLength={500}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs resize-none"
              />
            </div>
          </div>
        </div>

        {/* Sidebar Overview Card */}
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-xs flex flex-col gap-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-1.5">
              <span className="material-symbols-outlined text-base text-primary">info</span>
              <span>Classification Guidance</span>
            </h3>

            <div className="text-xs text-on-surface-variant space-y-2.5">
              <p>
                <strong>Telephony Identification</strong>: Contact categories allow incoming callers to be matched against known stakeholder types automatically.
              </p>
              <p>
                <strong>CRM & Project Contacts</strong>: Team members can quickly segment phone numbers and emails by relationship (Client, Vendor, Consultant).
              </p>
            </div>

            <div className="rounded-xl bg-surface-container p-3 text-[11px] text-on-surface-variant flex flex-col gap-1 border border-outline-variant/15">
              <span className="font-bold text-on-surface">Data Integrity:</span>
              <span>Duplicate names are prevented. Deletion uses audit-backed soft deletion to ensure historic communications remain traceable.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
